import {
    useCallback,
    useEffect,
    useState,
    FormEventHandler,
    ReactElement,
    useRef
} from 'react';
import {
    UICreatableMultiSelectInput,
    UIFormInput,
    UIFormInputRef,
    UILocationPicker,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIQRScanner,
    UISwitch,
    UIText,
    UIToast
} from '@/ui';
import { BeaconLocation, CreateBeaconRequest } from '@/api';
import QrScanner from 'qr-scanner';
import { DownloadApp } from '@/common/components';
import { useCreateBeaconAction } from '../actions';
import { useDevicesTranslation } from '../hooks';

type CreateBeaconModalProps = {
    availableTags: string[];
    companyId: string;
    isOpen: boolean;
    onClose: () => void;
};

export function CreateBeaconModal(props: CreateBeaconModalProps): ReactElement {
    const { availableTags, companyId, isOpen, onClose } = props;

    const createBeacon = useCreateBeaconAction();

    function handleClose() {
        onClose();
    }

    function onSubmit(data: CreateBeaconRequest) {
        createBeacon
            .createBeacon(data)
            .then(() => {
                handleClose();
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {/* Mounted only while open so the form starts empty each time. */}
            {isOpen && (
                <FormContent
                    companyId={companyId}
                    onClose={handleClose}
                    loading={createBeacon.loading}
                    onSubmit={onSubmit}
                    availableTags={availableTags}
                />
            )}
        </UIModal>
    );
}

type FormContentProps = {
    companyId: string;
    availableTags: string[];
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: CreateBeaconRequest) => void;
};

function FormContent(props: FormContentProps): ReactElement {
    const { companyId, availableTags, loading, onClose, onSubmit } = props;
    const { t } = useDevicesTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Mac
     */
    const macRef = useRef<UIFormInputRef>(null);
    const [mac, setMac] = useState('');
    const [macError, setMacError] = useState('');
    const checkMac = useCallback(
        (mac: string): string => {
            const macTrimmed = mac.trim();
            const macAddressRegex =
                /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$|^([0-9A-Fa-f]{12})$/;
            if (!macAddressRegex.test(macTrimmed)) {
                return t('shared.fields.mac.error_invalid');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setMacError(checkMac(mac));
        }
    }, [mac, showRealTimeErrors, checkMac]);

    /**
     * Name
     */
    const nameRef = useRef<UIFormInputRef>(null);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const checkName = useCallback(
        (name: string): string => {
            const nameTrimmed = name.trim();
            if (nameTrimmed.length === 0) {
                return t('shared.fields.name.error_required');
            } else if (nameTrimmed.length > 1000) {
                return t('shared.fields.name.error_too_long');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setNameError(checkName(name));
        }
    }, [name, showRealTimeErrors, checkName]);

    /**
     * Tags
     */
    type TagOption = { value: string; label: string };
    const [tags, setTags] = useState<TagOption[]>([]);
    const [tagsError, setTagsError] = useState('');
    const tagsOptions: TagOption[] = availableTags.map((tag) => ({
        value: tag,
        label: tag
    }));
    const checkTags = useCallback(
        (tags: TagOption[]): string => {
            if (tags.length > 4) {
                return t('shared.fields.tags.error_too_many');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTagsError(checkTags(tags));
        }
    }, [tags, showRealTimeErrors, checkTags]);

    /**
     * Location
     */
    const [useLocation, setUseLocation] = useState(false);
    const [location, setLocation] = useState<BeaconLocation | null>(null);
    const [locationHasError, setLocationHasError] = useState(false);

    /**
     * Mac Address QR scanning
     */
    const [hasCamera, setHasCamera] = useState<boolean | null>(null);
    useEffect(() => {
        QrScanner.hasCamera()
            .then((hasCamera) => {
                setHasCamera(hasCamera);
            })
            .catch(() => {
                UIToast.error(
                    t('components.create_beacon_modal.error_checking_camera')
                );
            });
    }, [t]);
    const [isMacScanning, setIsMacScanning] = useState(false);
    function handleScannedMac(data: string) {
        // Data should look like this: https://device.safepointapp.com/?beacon_mac=abc123
        // We need to take the value of "beacon_mac" from the data
        let mac: string | null = null;

        function takeMacFromUrl(data: string): string | null {
            try {
                const url = new URL(data);
                return url.searchParams.get('beacon_mac');
            } catch {
                return null;
            }
        }

        function takeFromSmallBeaconQRCode(data: string): string | null {
            // Data might also look like: M1=E3:2B:B8:DC:45:E8
            if (data.substring(0, 3) === 'M1=') {
                return data.substring(3);
            } else {
                return null;
            }
        }

        mac = takeMacFromUrl(data) || takeFromSmallBeaconQRCode(data);

        if (mac && checkMac(mac) === '') {
            setMac(mac);
            setIsMacScanning(false);
            UIToast.success(t('components.create_beacon_modal.mac_scanned'));
        } else {
            UIToast.error(t('shared.errors.invalid_qr_code'));
        }
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkMac(mac) && !checkName(name) && !checkTags(tags)) {
            if (useLocation && locationHasError) {
                return;
            }

            setShowRealTimeErrors(false);
            onSubmit?.({
                companyId,
                mac,
                name,
                tags: tags.map((tag) => tag.value),
                location: useLocation ? location : null
            });
        }
    };

    if (isMacScanning && hasCamera === false) {
        return (
            <>
                <UIModalHeader
                    title={t('components.create_beacon_modal.no_camera_title')}
                    subtitle={t(
                        'components.create_beacon_modal.no_camera_subtitle'
                    )}
                />
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 16
                    }}
                >
                    <DownloadApp />
                    <a
                        href="https://support.safepointapp.com"
                        target="_blank"
                        style={{ textDecoration: 'none' }}
                    >
                        <UIText type="link">
                            {t(
                                'components.create_beacon_modal.no_camera_instructions'
                            )}
                        </UIText>
                    </a>
                </div>
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.labels.back'),
                            onClick: () => {
                                setIsMacScanning(false);
                            }
                        }
                    ]}
                />
            </>
        );
    }

    if (isMacScanning) {
        return (
            <>
                <UIModalHeader title={t('shared.labels.scan_qr_code')} />
                <UIQRScanner onScan={handleScannedMac} />
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.labels.back'),
                            onClick: () => {
                                setIsMacScanning(false);
                            }
                        }
                    ]}
                />
            </>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <UIModalHeader
                    title={t('components.create_beacon_modal.title')}
                />
                <UIModalContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <UIFormInput
                            ref={macRef}
                            name="mac"
                            label={t('shared.fields.mac.label')}
                            value={mac}
                            description={t('shared.fields.mac.description')}
                            type="text"
                            autoComplete="on"
                            errorText={showRealTimeErrors ? macError : ''}
                            required
                            maxLength={1000}
                            onChange={setMac}
                            autoFocus
                            labelButton={
                                hasCamera !== null
                                    ? {
                                          text: t('shared.labels.scan_qr_code'),
                                          onClick: () => {
                                              setIsMacScanning(true);
                                          }
                                      }
                                    : undefined
                            }
                        />

                        <UIFormInput
                            ref={nameRef}
                            name="name"
                            label={t('shared.fields.name.label')}
                            value={name}
                            description={t('shared.beacons.name_description')}
                            type="text"
                            autoComplete="on"
                            errorText={showRealTimeErrors ? nameError : ''}
                            required
                            maxLength={1000}
                            onChange={setName}
                        />

                        <UICreatableMultiSelectInput
                            label={t('shared.fields.tags.label')}
                            placeholder={t('shared.fields.tags.placeholder')}
                            description={t('shared.beacons.tags_description')}
                            required={false}
                            options={tagsOptions}
                            value={tags}
                            onChange={(selectedOptions) => {
                                setTags(selectedOptions as TagOption[]);
                            }}
                            errorText={showRealTimeErrors ? tagsError : ''}
                        />

                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                justifyContent: 'space-between'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t('shared.fields.location.label')}
                                </UIText>
                                <UIText variant="small" type="default2">
                                    {t('shared.beacons.location_description')}
                                </UIText>
                            </div>

                            <UISwitch
                                checked={useLocation}
                                onChange={setUseLocation}
                            />
                        </div>
                        {useLocation && (
                            <UILocationPicker
                                showErrors={showRealTimeErrors}
                                onLocationChange={(lat, lng, isValid) => {
                                    setLocation({ lat, lng });
                                    setLocationHasError(!isValid);
                                }}
                            />
                        )}
                    </div>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: t('shared.labels.submit')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            disabled: loading,
                            onClick: onClose
                        }
                    ]}
                />
            </form>
        </>
    );
}
